import 'regenerator-runtime/runtime';
import {Store} from 'redux';
import Container from '../Container/Container';
import {ClassOrConstructor, Constructable} from '@src/@types/instantable';
import {StringArray} from '@src/@interfaces/types';
import {IApp, IProvider} from '@src/@interfaces/services/App';
import {IReduxAction} from '@src/@interfaces';
import {IDispatcher, IEvent, IListener, ISubscription} from '@src/@interfaces/services/Events';
import {isConstructor} from '@src/helpers/isConstructor';
import {IHttp} from '@src/@interfaces/services/Http/IHttp';
import {IContainedServices} from '@src/@interfaces/services/Container/IContainedServices';
import {IConfig} from '@src/@interfaces/services/Config/IConfig';
import {EventPayload} from '@src/@types/services/Events/EventPayload';

const api = new Container();

export default class App implements IApp {
    protected providers: (IProvider | Constructable<IProvider>)[] = [];
    private booted = false;
    private loaded = false;

    isBooted(): boolean {
        return this.booted;
    }

    isLoaded(): boolean {
        return this.loaded;
    }

    // config

    get config(): IConfig {
        return this.make<IConfig>('config') as IConfig;
    }

    // booting

    async boot(): Promise<void> {
        if (this.booted) {
            return;
        }

        const awaitQueue = [];
        for (const provider of this.getSortedProviders()) {
            if (provider.shouldAwait) {
                await provider.boot(this);

                continue;
            }

            if (provider.holdBootUntilNotCompleted) {
                awaitQueue.push(
                    provider.boot(this)
                );
            } else {
                // noinspection ES6MissingAwait
                provider.boot(this);
            }
        }

        return Promise.all(awaitQueue).then(() => {
            this.booted = true;
        });
    }

    async loadRequires(): Promise<void> {
        if (this.loaded) {
            return;
        }

        const httpClient = this.make<IHttp>('http') as IHttp;

        return Promise.all(
            this.getSortedProviders().map(provider => provider.loading(httpClient))
        ).then(() => {
            this.loaded = true;
        });
    }

    registerProvider(provider: IProvider | Constructable<IProvider>): IApp {
        this.providers.push(provider);

        return this;
    }

    protected getSortedProviders(): IProvider[] {
        return this.resolveProviders().sort((a, b) => {
            return b.priority - a.priority;
        });
    }

    protected resolveProviders(): IProvider[] {
        return this.providers = this.providers.map(item => {
            return isConstructor(item) ? new item(this) : item;
        });
    }

    // events

    fireEvent(event: IEvent | string, payload?: EventPayload): any | null {
        return this.make<IDispatcher>('events')?.dispatch(event, payload);
    }

    listen(eventName: string | string[], listener: IListener): ISubscription | undefined {
        return this.make<IDispatcher>('events')?.listen(eventName, listener);
    }

    dispatchAction(action: IReduxAction): IReduxAction | void {
        return this.make<Store>('store')?.dispatch(action);
    }

    // container

    getStoreState(): any {
        return this.make<Store>('store')?.getState() ?? null;
    }

    declareBindings(bindings: IContainedServices | StringArray<any>): IApp {
        for (const [key, service] of Object.entries(bindings)) {
            this.bind(key, service);
        }

        return this;
    }

    has(abstract: string): boolean {
        return api.has(abstract);
    }

    bind<C>(abstract: string, concrete: ClassOrConstructor<C>): IApp {
        api.bind(abstract, concrete);

        return this;
    }

    make<C>(abstract: string, args: any[] = []): C | null {
        return api.get(abstract, args);
    }
}
